import { useState, useCallback, useMemo } from "react"

export type NumericInputValue = number | ""

export interface UseNumericInputOptions {
  min?: number
  max?: number
  integer?: boolean
  /** Initial numeric value; empty string means no value */
  initialValue?: NumericInputValue
  /** Controlled numeric value; if provided, the hook mirrors this */
  value?: NumericInputValue
  /** Custom validator; return a message if invalid */
  validate?: (value: NumericInputValue) => string | undefined
}

export interface UseNumericInputResult {
  inputValue: string
  numericValue: NumericInputValue
  handleChange: (event: { target: { value: string } }) => void
  handleBlur: () => void
  error?: string
  setNumericValue: (value: NumericInputValue) => void
}

export function useNumericInput(options: UseNumericInputOptions = {}): UseNumericInputResult {
  const { min, max, integer, initialValue = "", value, validate } = options

  const runValidation = useCallback(
    (val: NumericInputValue): string | undefined => {
      if (val === "") {
        // Default required; callers can override with validate
        if (validate) {
          return validate(val)
        }
        return "Required"
      }

      if (Number.isNaN(val)) {
        return "Invalid number"
      }

      if (integer && !Number.isInteger(val)) {
        return "Must be an integer"
      }

      if (min != null && val < min) {
        return `Must be >= ${min}`
      }

      if (max != null && val > max) {
        return `Must be <= ${max}`
      }

      if (validate) {
        return validate(val)
      }

      return undefined
    },
    [integer, max, min, validate],
  )

  // Compute derived state from controlled or uncontrolled value
  const derivedState = useMemo(() => {
    const source = value !== undefined ? value : initialValue
    if (source === "") {
      return { numericValue: "" as NumericInputValue, inputValue: "", error: runValidation("") }
    }
    const n = Number(source)
    if (Number.isNaN(n)) {
      return { numericValue: "" as NumericInputValue, inputValue: "", error: "Invalid number" }
    }
    return { numericValue: n, inputValue: String(n), error: runValidation(n) }
  }, [value, initialValue, runValidation])

  // Initialize and sync state based on controlled/uncontrolled mode
  const [numericValue, setNumericValueState] = useState<NumericInputValue>(derivedState.numericValue)
  const [inputValue, setInputValue] = useState<string>(derivedState.inputValue)
  const [error, setError] = useState<string | undefined>(derivedState.error)

  // In controlled mode, return the derived state; in uncontrolled mode, return the component state
  const finalNumericValue = value !== undefined ? derivedState.numericValue : numericValue
  const finalInputValue = value !== undefined ? derivedState.inputValue : inputValue
  const finalError = value !== undefined ? derivedState.error : error

  const handleChange: UseNumericInputResult["handleChange"] = useCallback(
    (event) => {
      const raw = event.target.value

      if (raw === "") {
        setInputValue("")
        setNumericValueState("")
        setError(runValidation(""))
        return
      }

      const maybeNumber = Number(raw)
      if (Number.isNaN(maybeNumber)) {
        setInputValue(raw)
        setError("Invalid number")
        return
      }

      const coerced = maybeNumber
      setInputValue(raw)
      setNumericValueState(coerced)
      setError(runValidation(coerced))
    },
    [runValidation],
  )

  const handleBlur = useCallback(() => {
    setError(runValidation(finalNumericValue))
  }, [finalNumericValue, runValidation])

  const setNumericValue = useCallback(
    (val: NumericInputValue) => {
      if (val === "") {
        setNumericValueState("")
        setInputValue("")
        setError(runValidation(""))
      } else {
        const n = Number(val)
        if (Number.isNaN(n)) {
          setNumericValueState("")
          setInputValue("")
          setError("Invalid number")
        } else {
          setNumericValueState(n)
          setInputValue(String(n))
          setError(runValidation(n))
        }
      }
    },
    [runValidation],
  )

  return {
    inputValue: finalInputValue,
    numericValue: finalNumericValue,
    handleChange,
    handleBlur,
    error: finalError,
    setNumericValue,
  }
}
